import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Container, Row, Col, Card, Badge } from 'react-bootstrap';
import { motion } from 'framer-motion';
import SEO from '../../components/common/SEO';
import { getProductBySlug, products, productCategories } from '../../data/products';

const ProductDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const product = getProductBySlug(slug || '');

  const [selectedImage, setSelectedImage] = useState(0);

  if (!product) {
    return <Navigate to="/products" replace />;
  }

  const allImages = product.images;

  // Get related products from same category (excluding current)
  const relatedProducts = products
    .filter(p => p.category === product.category && p.id !== product.id)
    .slice(0, 3);

  // Get category label
  const categoryInfo = productCategories.find(c => c.id === product.category);

  return (
    <>
      <SEO
        title={product.seoTitle}
        description={product.seoDescription}
        keywords={`${product.name}, skytion enterprises, ${product.brand}, tissue products, ${product.specs.material}`}
      />

      {/* Hero Section */}
      <section className="bg-light py-5">
        <Container>
          <Row className="align-items-center">
            <Col lg={6}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="d-flex gap-2 mb-3">
                  <Badge bg="primary">{product.brand}</Badge>
                  <Badge bg="secondary">{categoryInfo?.name || product.categoryLabel}</Badge>
                </div>
                <h1 className="display-4 mb-3">{product.name}</h1>
                <p className="lead mb-4">{product.description}</p>
                <div className="d-flex gap-2 mb-4 flex-wrap">
                  <Badge bg="success">{product.specs.ply} Ply</Badge>
                  <Badge bg="info">{product.specs.material}</Badge>
                  {product.specs.weight && <Badge bg="warning">{product.specs.weight}</Badge>}
                  {product.specs.mrp && <Badge bg="dark">MRP {product.specs.mrp}</Badge>}
                </div>
              </motion.div>
            </Col>
            <Col lg={6}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <div className="product-image-gallery">
                  <img
                    src={allImages[selectedImage]?.src || ''}
                    alt={allImages[selectedImage]?.alt || product.name}
                    className="img-fluid rounded shadow main-product-image"
                    style={{ width: '100%', height: '400px', objectFit: 'contain', background: '#f8f9fa' }}
                  />
                  {allImages.length > 1 && (
                    <div className="image-thumbnails mt-3 d-flex gap-2 justify-content-center flex-wrap">
                      {allImages.map((img, index) => (
                        <img
                          key={index}
                          src={img.src}
                          alt={img.alt}
                          className={`thumbnail ${selectedImage === index ? 'active' : ''}`}
                          style={{
                            width: '60px',
                            height: '60px',
                            objectFit: 'contain',
                            background: '#f8f9fa',
                            border: selectedImage === index ? '2px solid #2563eb' : '1px solid #dee2e6',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          onClick={() => setSelectedImage(index)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Specifications */}
      <section className="py-5">
        <Container>
          <Row>
            <Col lg={8}>
              <Card className="shadow-sm mb-4">
                <Card.Header className="bg-primary text-white">
                  <h3 className="mb-0">Product Specifications</h3>
                </Card.Header>
                <Card.Body>
                  <div className="product-specs-grid">
                    <Row className="g-3">
                      {product.specs.dimension && (
                        <Col md={6}>
                          <div className="spec-item">
                            <strong className="text-primary">Dimensions:</strong><br />
                            <span className="h6">{product.specs.dimension}</span>
                          </div>
                        </Col>
                      )}
                      <Col md={6}>
                        <div className="spec-item">
                          <strong className="text-primary">Material:</strong><br />
                          <span className="h6">{product.specs.material}</span>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="spec-item">
                          <strong className="text-primary">Ply:</strong><br />
                          <Badge className="specs-badge">{product.specs.ply} Ply</Badge>
                        </div>
                      </Col>
                      {product.specs.weight && (
                        <Col md={6}>
                          <div className="spec-item">
                            <strong className="text-primary">Weight:</strong><br />
                            <span className="h6">{product.specs.weight}</span>
                          </div>
                        </Col>
                      )}
                      {product.specs.sheets && (
                        <Col md={6}>
                          <div className="spec-item">
                            <strong className="text-primary">Sheets:</strong><br />
                            <span className="h6">{product.specs.sheets}</span>
                          </div>
                        </Col>
                      )}
                      {product.specs.mrp && (
                        <Col md={6}>
                          <div className="spec-item">
                            <strong className="text-primary">MRP:</strong><br />
                            <span className="h6 text-success">{product.specs.mrp}</span>
                          </div>
                        </Col>
                      )}
                    </Row>
                  </div>
                </Card.Body>
              </Card>

              <Card className="shadow-sm mb-4">
                <Card.Header className="bg-success text-white">
                  <h3 className="mb-0">Key Features</h3>
                </Card.Header>
                <Card.Body>
                  <ul className="feature-list">
                    {product.features.map((feature, index) => (
                      <li key={index}>
                        <span className="text-success me-3 fw-bold">✓</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </Card.Body>
              </Card>

              <Card className="shadow-sm">
                <Card.Header className="bg-info text-white">
                  <h3 className="mb-0">Applications & Use Cases</h3>
                </Card.Header>
                <Card.Body>
                  <h5 className="mb-3">Primary Applications:</h5>
                  <div className="application-grid">
                    {product.specs.application.map((app, index) => (
                      <div key={index} className="application-item">
                        <span className="text-success me-2 fw-bold">✓</span>
                        {app}
                      </div>
                    ))}
                  </div>
                  <hr className="my-4" />
                  <h5 className="mb-3">Ideal For:</h5>
                  <ul className="feature-list">
                    {product.useCases.map((useCase, index) => (
                      <li key={index}>
                        <span className="text-primary me-3">▶</span>
                        {useCase}
                      </li>
                    ))}
                  </ul>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4}>
              <Card className="shadow-sm sticky-top" style={{ top: '20px' }}>
                <Card.Header className="bg-warning text-dark text-center">
                  <h4 className="mb-0">Get Quote</h4>
                </Card.Header>
                <Card.Body>
                  <div className="text-center mb-3">
                    <Badge className="quality-badge mb-3" bg="light" text="dark">
                      ✓ ISO Certified Quality
                    </Badge>
                  </div>
                  <p className="text-center">Get competitive pricing for bulk orders</p>
                  <div className="contact-info mb-4">
                    <div className="mb-3 p-3 bg-light rounded">
                      <strong>Phone:</strong><br />
                      <a href="tel:+918920065965" className="text-decoration-none">+91 8920065965</a>
                    </div>
                    <div className="mb-3 p-3 bg-light rounded">
                      <strong>Email:</strong><br />
                      <a href="mailto:sales@skytion.in" className="text-decoration-none">sales@skytion.in</a>
                    </div>
                    <div className="mb-3 p-3 bg-light rounded">
                      <strong>Manufacturer:</strong><br />
                      Skytion Enterprises<br />
                      <small className="text-muted">Noida, India</small>
                    </div>
                  </div>
                  <Link to="/contact" className="btn btn-primary w-100 mb-2">
                    Request Quote
                  </Link>
                  <small className="text-muted d-block text-center">
                    Custom pricing based on quantity
                  </small>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <section className="py-5 bg-light">
          <Container>
            <Row>
              <Col lg={12} className="text-center mb-4">
                <h3>Related Products</h3>
                <p className="text-muted">Explore other products from {categoryInfo?.name}</p>
              </Col>
            </Row>
            <Row>
              {relatedProducts.map((relatedProduct, index) => (
                <Col lg={4} md={6} key={relatedProduct.id} className="mb-4">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <Card className="h-100 shadow-sm hover-card">
                      <div className="p-3 text-center">
                        <img
                          src={relatedProduct.images[0]?.src || ''}
                          alt={relatedProduct.name}
                          style={{ width: '100px', height: '100px', objectFit: 'contain' }}
                          className="mb-3"
                        />
                      </div>
                      <Card.Body className="text-center">
                        <Card.Title className="h5">{relatedProduct.name}</Card.Title>
                        <Badge bg="outline-primary" className="mb-3">{relatedProduct.brand}</Badge>
                        <div>
                          <Link to={`/products/${relatedProduct.slug}`} className="btn btn-outline-primary btn-sm">
                            View Details
                          </Link>
                        </div>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
            <Row>
              <Col lg={12} className="text-center mt-3">
                <Link to="/products" className="btn btn-primary">
                  View All Products
                </Link>
              </Col>
            </Row>
          </Container>
        </section>
      )}
    </>
  );
};

export default ProductDetail;
